<script lang="ts">
  import { offline } from "../lib/state";
  import { BUILD_INFO } from "../lib/buildInfo";
  import { refetchWorkouts } from "../lib/workoutsStore";

  const labelFor = (v: typeof $offline) => {
    if (v === "ok") return null;
    if (v === "no-handler") return "preview";
    if (v === "timeout") return "offline";
    if (v === "stub") return "preview";
    // Health declined: nothing was saved and a refresh cannot help; the Swim tab's
    // empty card says Health is off (beta-review panel, 2026-09-13, 5/5).
    if (v === "health-off") return null;
    // Jargon-free + tap affordance (VP panel 2026-09-01: "cached" read as
    // leaked debug text to 5/5 reviewers, and the retry tap was invisible).
    return "saved copy · tap to refresh";
  };

  let label = $derived(labelFor($offline));
  let sha = $derived(BUILD_INFO.commitSha.slice(0, 7));

  // Item 4: after a genuine failed load the user sat on stub/cached data with no
  // way to recover except a native nudge or relaunch. Make the badge an active
  // retry — tapping re-runs the workout-list fetch (bypasses the one-shot guard).
  let retrying = $state(false);
  async function retry() {
    if (retrying) return;
    retrying = true;
    try {
      await refetchWorkouts();
    } finally {
      retrying = false;
    }
  }
</script>

{#if label !== null}
  <button
    class="indicator"
    title="Build {sha} — tap to retry"
    aria-label="Data is {label}. Tap to reload your swims."
    onclick={retry}
    disabled={retrying}
    type="button"
  >
    <span class="dot" aria-hidden="true"></span>
    <span class="label">{retrying ? "retrying…" : label}</span>
  </button>
{/if}

<style>
  .indicator {
    position: fixed;
    left: 0.75rem;
    /* Bottom-left, same as before (keeps it clear of the FAB in the bottom-right
       corner) — but lifted above BottomNav's floating pill (~72px: dock's own
       offset + pill height, matching SaveTroublePill's clearance calc) so it
       never sits on top of the Swim tab. */
    bottom: calc(72px + var(--space-3) + env(safe-area-inset-bottom, 0px));
    z-index: 40;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.25rem 0.6rem;
    background: rgba(31, 31, 34, 0.85);
    border: 1px solid var(--divider-strong);
    border-radius: var(--radius-pill);
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    /* Now a real button (item 4): reset UA button chrome, keep the pill look,
       and signal it's tappable. */
    cursor: pointer;
    font-family: inherit;
    -webkit-tap-highlight-color: transparent;
  }
  .indicator:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  .indicator:disabled {
    cursor: default;
    opacity: 0.7;
  }
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent-yellow);
    /* Decorative colored glow removed (readability round, 2026-07-16) — this
       status dot isn't one of DESIGN.md's four named glows (Floating / Peak /
       PR / Moon); the label text next to it already carries the meaning. */
  }
</style>
